import { FC, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { ExitSurveyPopover } from './ExitSurveyPopover';
import { useTranslation } from '../../hooks/useTranslation';
import { useResolvedLandingVariant } from '../../hooks/useResolvedLandingVariant';
import { LandingVariant } from '../../models/landing/variant';
import { getVariantCopy } from '../../utils/landing/getVariantCopy';
import { LandingVariantProps } from './variants/types';
import { PhoneAndLink } from './variants/PhoneAndLink';
import { PhoneOnly } from './variants/PhoneOnly';
import { LinkOnly } from './variants/LinkOnly';
import { PhoneOnlyV1 } from './variants/PhoneOnlyV1';
import { PhoneAndLinkV1 } from './variants/PhoneAndLinkV1';
import { LinkOnlyV1 } from './variants/LinkOnlyV1';
import { PhoneOnlyV2 } from './variants/PhoneOnlyV2';

/**
 * Maps each design key to the component that renders it. Replacing the old
 * switch with a registry means adding a design is one map entry, not a new case.
 *
 * The unsuffixed keys are the live design; the `-v1` keys are the new-UX
 * redesign, so a locale can A/B the two via the flag payload's `split` arms
 * (and point `afterHours.lp` at either design's link-only page).
 * `phone-only-v2` is the LP1 hybrid (phone-only).
 */
const VARIANT_REGISTRY: Record<LandingVariant, FC<LandingVariantProps>> = {
  'phone-only': PhoneOnly,
  'phone-and-link': PhoneAndLink,
  'link-only': LinkOnly,
  'phone-only-v1': PhoneOnlyV1,
  'phone-and-link-v1': PhoneAndLinkV1,
  'link-only-v1': LinkOnlyV1,
  'phone-only-v2': PhoneOnlyV2,
};

export const LandingPage: FC = () => {
  const { locale, data } = useTranslation();
  const posthog = usePostHog();
  // A CTA click cancels the pending exit survey (see ExitSurveyPopover):
  // someone who acted is not the "landed and did nothing" audience it asks
  // about.
  const [ctaClicked, setCtaClicked] = useState(false);

  // The LP the user actually saw, resolved by the shared hook that
  // PostHogPageView also uses — so what is rendered and what is reported as
  // `resolved_lp` can never disagree.
  const { variant } = useResolvedLandingVariant();

  // `resolved_lp` / `configured_lp` are registered by PostHogPageView, which is
  // mounted as an earlier sibling of the <Outlet/> that renders this page.
  // React runs sibling effects in tree order, so registering them here lost the
  // race to that component's `$pageview` capture and left the pageview
  // unattributed. See services/posthog/PageView.tsx.

  if (!locale || !data) {
    return null;
  }

  // No consent gate: analytics run cookielessly (see services/posthog/init.ts),
  // so nothing is stored on the device and every click is counted.
  const onCtaClick = (cta: 'phone' | 'online', destination: string) => {
    setCtaClicked(true);
    posthog.capture('cta_click', {
      cta,
      variant,
      locale,
      brand: import.meta.env.VITE_BRAND_CODE,
      destination,
    });
  };

  const variantProps = { data, copy: getVariantCopy(data, variant), onCtaClick };

  const Variant = VARIANT_REGISTRY[variant];
  return (
    <>
      <Variant {...variantProps} />
      <ExitSurveyPopover ctaClicked={ctaClicked} locale={locale} />
    </>
  );
};
